/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Equipment.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Equipment } from "./Equipment";

import { CalendarEventResource } from "./CalendarEventResource";
import { DropoffHasEquipment } from "./DropoffHasEquipment";
import { MapObject } from "./MapObject";
import { NotificationList } from "./NotificationList";
import { Project } from "./Project";

export type attachmentMethod = "file" | "iframe";

export const attachmentMethodValues = { file: 0, iframe: 1 };

export abstract class EquipmentModel {
    id: number;
    archived: boolean;
    attachmentMethod: attachmentMethod;
    createdAt: Dayjs;
    details: string | null;
    equipmentIframe: string | null;
    equipmentName: string | null;
    equipmentType: string | null;
    matchJobsiteHours: boolean;
    projectId: number | null;
    updatedAt: Dayjs;
    repeatingCalendarEventDefinitions: any | null;
    calendarEventResources: CalendarEventResource[] | undefined;
    dropoffHasEquipment: DropoffHasEquipment[] | undefined;
    mapObject: MapObject | null | undefined;
    notificationLists: NotificationList[] | undefined;
    project: Project | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        archived: boolean,
        attachmentMethod: attachmentMethod,
        createdAt: Dayjs,
        details: string | null,
        equipmentIframe: string | null,
        equipmentName: string | null,
        equipmentType: string | null,
        matchJobsiteHours: boolean,
        projectId: number | null,
        updatedAt: Dayjs,
        repeatingCalendarEventDefinitions: any | null,
        calendarEventResources: CalendarEventResource[] | undefined,
        dropoffHasEquipment: DropoffHasEquipment[] | undefined,
        mapObject: MapObject | null | undefined,
        notificationLists: NotificationList[] | undefined,
        project: Project | null | undefined
    ) {
        this.id = id;
        this.archived = archived;
        this.attachmentMethod = attachmentMethod;
        this.createdAt = createdAt;
        this.details = details;
        this.equipmentIframe = equipmentIframe;
        this.equipmentName = equipmentName;
        this.equipmentType = equipmentType;
        this.matchJobsiteHours = matchJobsiteHours;
        this.projectId = projectId;
        this.updatedAt = updatedAt;
        this.repeatingCalendarEventDefinitions = repeatingCalendarEventDefinitions;
        this.calendarEventResources = calendarEventResources;
        this.dropoffHasEquipment = dropoffHasEquipment;
        this.mapObject = mapObject;
        this.notificationLists = notificationLists;
        this.project = project;
    }
    /* eslint-enable max-statements */

    /** Rails `alias_attribute :name, :equipment_name` */
    get name(): string | null {
        return this.equipmentName;
    }

    set name(value: string | null) {
        this.equipmentName = value;
    }

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): Equipment {
        const newObject = new Equipment(
            json["id"],
            json["archived"],
            json["attachment_method"],
            dayjs(json["created_at"]).tz(timezone),
            json["details"],
            json["equipment_iframe"],
            json["equipment_name"],
            json["equipment_type"],
            json["match_jobsite_hours"],
            json["project_id"],
            dayjs(json["updated_at"]).tz(timezone),
            json["repeating_calendar_event_definitions"],
            json["calendar_event_resources"]?.map((item: any) => CalendarEventResource.fromApiObject(item, timezone)),
            json["dropoff_has_equipment"]?.map((item: any) => DropoffHasEquipment.fromApiObject(item, timezone)),
            json["map_object"] == null ? json["map_object"] : MapObject.fromApiObject(json["map_object"], timezone),
            json["notification_lists"]?.map((item: any) => NotificationList.fromApiObject(item, timezone)),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(visited = new Set<any>()): any {
        if (visited.has(this)) {
            // Return null, undefined, or primary key to break the cycle
            return undefined;
        }
        visited.add(this);

        return {
            id: this.id,
            archived: this.archived,
            attachment_method: this.attachmentMethod,
            created_at: this.createdAt,
            details: this.details,
            equipment_iframe: this.equipmentIframe,
            equipment_name: this.equipmentName,
            equipment_type: this.equipmentType,
            match_jobsite_hours: this.matchJobsiteHours,
            project_id: this.projectId,
            updated_at: this.updatedAt,
            calendar_event_resources_attributes: this.calendarEventResources
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            dropoff_has_equipment: this.dropoffHasEquipment
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            map_object_attributes: this.mapObject?.toApiObject(visited),
            notification_lists: this.notificationLists
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            project_attributes: this.project?.toApiObject(visited),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Equipment {
        const clone = Object.create(this) as Equipment;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
